import { User } from '@auth0/auth0-react';
import axios, { AxiosResponse } from 'axios';
import SessionClass from './SessionClass';
import { getPartnerByEmail } from './registration_service/getRequests';
import { UserRole } from '../types/role';

/**
 * Persists the app's own session markers derived from an authenticated
 * Auth0 user + access token: sessionStorage `user`/`user token` and
 * localStorage `email`. Shared between the OAuth callback page
 * (Auth.tsx) and the route guard that hydrates a fresh tab which
 * inherited a still-valid Auth0 session (via the shared, localStorage-
 * cached token) without ever visiting that callback.
 */
export const hydrateSession = (user: User, accessToken: string): void => {
  sessionStorage.setItem('usertoken', accessToken);
  new SessionClass().setUser('user', user);
  if (user.email) {
    localStorage.setItem('email', user.email);
  }
};

const ROLE_TO_PATH: Record<string, string> = {
  [UserRole.SHOP_OWNER]: 'shopOwners',
  [UserRole.BARBER]: 'barbers',
  [UserRole.SHOP_BARBER]: 'shopBarbers',
};

// Tags which authenticated user + role the currently-stored
// `reg_partner_id`/`reg_shop_id` belong to. Auth0 session state
// (isAuthenticated) can transition — expiry, a logout path that clears
// `role`/`email` but intentionally leaves `reg_*` alone for in-progress
// registration flows, another tab's session, etc. — without those
// identifiers ever being cleared. Without this fingerprint, a
// subsequent sign-in (same browser, different account, or the same
// account under a different role) would see a non-null `reg_partner_id`
// and skip re-resolving it, silently reusing the previous session's
// partner/shop IDs for protected requests.
const PARTNER_DATA_OWNER_KEY = 'reg_partner_owner';

const partnerDataOwnerFingerprint = (email: string, role: string): string =>
  `${email}::${role}`;

/**
 * True only if `reg_partner_id`/`reg_shop_id` were resolved for this
 * exact email + role combination. Callers should treat any other
 * result (missing IDs, or IDs tagged for a different user/role) as
 * unresolved and re-fetch via hydratePartnerData.
 */
export const isPartnerDataCurrent = (email: string, role: string): boolean =>
  localStorage.getItem('reg_partner_id') !== null &&
  (role === UserRole.BARBER || localStorage.getItem('reg_shop_id') !== null) &&
  localStorage.getItem(PARTNER_DATA_OWNER_KEY) ===
    partnerDataOwnerFingerprint(email, role);

/**
 * Thrown only when the registration API definitively confirms there's
 * no partner record for this email/role combination (a 404) — i.e. the
 * user genuinely selected a role that doesn't apply to their account.
 * Any other rejection (network failure, 5xx, timeout, etc.) is
 * rethrown as-is so callers don't mistake a transient API outage for a
 * role mismatch.
 */
export class PartnerNotFoundError extends Error {
  constructor(email: string, role: string) {
    super(`No ${role} partner found for ${email}`);
    this.name = 'PartnerNotFoundError';
  }
}

/**
 * Resolves the `reg_partner_id`/`reg_shop_id` identifiers that
 * downstream routes/components (Dashboard, ProfileDataProvider,
 * ReservationManager, etc.) read directly from localStorage. This used
 * to run as a fire-and-forget effect in HeaderBanner, independent of
 * the app's readiness gate — so a fresh login (or a fresh tab) could
 * mount those protected routes before the lookup resolved, leaving
 * them reading empty/stale IDs. Awaiting this as part of session
 * hydration ensures `ready` (see useHydratedSession) doesn't flip true
 * until these identifiers are actually available.
 *
 * Throws `PartnerNotFoundError` only for a definitive "no such
 * partner" (404) response — a genuine wrong-role selection. Any other
 * failure (network error, 5xx, timeout) is rethrown unchanged so the
 * caller can treat it as retryable instead of ending a valid Auth0
 * session over a transient outage.
 */
export const hydratePartnerData = async (
  email: string,
  role: string,
  accessToken: string,
  isStale: () => boolean = () => false
): Promise<void> => {
  const userRole = ROLE_TO_PATH[role];
  if (!userRole) {
    return;
  }

  let response: AxiosResponse;
  try {
    response = await getPartnerByEmail(email, userRole, accessToken);
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 404) {
      throw new PartnerNotFoundError(email, role);
    }
    throw err;
  }

  // shopOwners/shopBarbers responses are expected to carry a shop
  // reference, but the API can omit it (undefined `shops`/`shopId`) or
  // return it empty (a shop owner with no shops). Resolve it up front,
  // from a normalized shape, so a missing/empty collection can't throw
  // mid-hydration and block login.
  let shopId: bigint | undefined;
  if (userRole === 'shopOwners') {
    const shops = Array.isArray(response.data.shops) ? response.data.shops : [];
    // Match the existing convention elsewhere (registration uses
    // `shops[shops.length - 1]`) of treating the last shop as the
    // active selection for a multi-shop owner, not the first.
    const validShops = shops.filter(
      (shop: { id?: bigint }) => shop?.id !== undefined && shop?.id !== null
    );
    const lastShop = validShops[validShops.length - 1];
    if (!lastShop) {
      // A shop owner record with no associated shop isn't valid
      // partner data for this role — treat it the same as "no partner
      // found" rather than silently reusing a stale reg_shop_id.
      throw new PartnerNotFoundError(email, role);
    }
    shopId = lastShop.id;
  } else if (userRole === 'shopBarbers') {
    const rawShopId = response.data.shopId;
    if (rawShopId === undefined || rawShopId === null) {
      throw new PartnerNotFoundError(email, role);
    }
    shopId = rawShopId;
  }

  // The awaited request may have outlived the caller's interest in its
  // result (e.g. the user logged out, switched accounts, or a retry
  // superseded this attempt while it was in flight). Applying this
  // write anyway would silently repopulate reg_* for the wrong
  // account. `isStale` lets the caller veto the write with up-to-date
  // information, checked only now — right before mutating storage —
  // rather than at call time.
  //
  // `isStale` alone only reflects *this* hook instance's own cleanup
  // (its effect unmounted/reran). `email`/`role` are shared across
  // tabs via localStorage, so a request started under one account can
  // still resolve after a *different* tab switches accounts while this
  // instance stays mounted — `cancelled` would remain false the whole
  // time. Re-checking the still-shared `email`/`role` markers here
  // catches that case too: if they no longer match what this call was
  // resolving for, some other tab/instance has since taken over and
  // this result is for a superseded account.
  if (
    isStale() ||
    localStorage.getItem('email') !== email ||
    localStorage.getItem('role') !== role
  ) {
    return;
  }

  const partnerId: bigint = response.data.id;
  localStorage.setItem('reg_partner_id', partnerId.toString());
  if (shopId !== undefined) {
    localStorage.setItem('reg_shop_id', shopId.toString());
  } else {
    // BARBER never had a shop; clear out any stale value from a
    // previous role/account so isPartnerDataCurrent's presence check
    // for this fingerprint can't be satisfied by leftover data.
    localStorage.removeItem('reg_shop_id');
  }
  // Written last: everything above either succeeded or threw, so the
  // fingerprint only ever marks reg_partner_id/reg_shop_id as current
  // once both are known-good for this email/role.
  localStorage.setItem(
    PARTNER_DATA_OWNER_KEY,
    partnerDataOwnerFingerprint(email, role)
  );
};
